import { useEffect, useMemo, useState } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { subscriptionsApi, type UserSubscription, type SubscriptionPlan } from '@/api/subscriptions'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  Skeleton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  TablePagination,
  InputAdornment,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Chip,
  Breadcrumbs,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useMediaQuery } from '@mui/material'
import { format } from 'date-fns'
import { ru, enUS } from 'date-fns/locale'
import { Link } from 'react-router-dom'
import { ROUTES } from '@/utils/constants'
import { parseSortValue } from '@/utils/sort'
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as ActivateIcon,
  Pause as DeactivateIcon,
  Schedule as ExtendIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { type Dayjs } from 'dayjs'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { FiltersButton } from '@/components/common/FiltersButton'
import { getUserDisplayName } from '@/utils/userName'
import '@/styles/dashboard.css'

type AdminSubFilters = {
  plan_id?: number
  status?: 'active' | 'inactive' | 'pending_payment'
  expires_from?: string
  expires_to?: string
  sort?: string
  order?: 'asc' | 'desc'
  search?: string
  page: number
  per_page: number
}

export function AdminSubscriptionsPage() {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const dateLocale = i18n.language === 'ru' ? ru : enUS
  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'

  const [subscriptions, setSubscriptions] = useState<UserSubscription[]>([])
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 15, total: 0 })
  const [filters, setFilters] = useState<AdminSubFilters>({
    page: 1,
    per_page: 15,
    sort: 'expires_at',
    order: 'desc',
  })
  const [searchInput, setSearchInput] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(false)

  /**
   * Счётчик для кнопки «Фильтры»: план, статус и две границы срока — это
   * четыре независимых контрола свёрнутой панели, поэтому даты считаем
   * по отдельности. Поиск и сортировка видны в тулбаре и в счёт не идут.
   */
  const appliedFiltersCount = useMemo(() => {
    let count = 0
    if (filters.plan_id !== undefined) count += 1
    if (filters.status !== undefined) count += 1
    if (filters.expires_from) count += 1
    if (filters.expires_to) count += 1

    return count
  }, [filters.expires_from, filters.expires_to, filters.plan_id, filters.status])
  const [actioningId, setActioningId] = useState<number | null>(null)
  const [extendDialogOpen, setExtendDialogOpen] = useState(false)
  const [extendDays, setExtendDays] = useState('7')
  const [extendSubmitting, setExtendSubmitting] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuSub, setMenuSub] = useState<UserSubscription | null>(null)
  const [extendOneDialogOpen, setExtendOneDialogOpen] = useState(false)
  const [extendOneSub, setExtendOneSub] = useState<UserSubscription | null>(null)
  const [extendOneDays, setExtendOneDays] = useState('7')
  const [extendOneSubmitting, setExtendOneSubmitting] = useState(false)

  const loadPlans = async () => {
    try {
      const list = await subscriptionsApi.getPlans()
      setPlans(Array.isArray(list) ? list : [])
    } catch {
      setPlans([])
    }
  }

  const loadSubscriptions = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await subscriptionsApi.getAdminSubscriptions({
        plan_id: filters.plan_id,
        status: filters.status,
        expires_from: filters.expires_from || undefined,
        expires_to: filters.expires_to || undefined,
        sort: filters.sort,
        order: filters.order,
        search: filters.search,
        page: filters.page,
        per_page: filters.per_page,
      })
      setSubscriptions(res.data)
      setMeta(res.meta)
    } catch (err: unknown) {
      const e = err as { message?: string }
      setError(e?.message || t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPlans()
  }, [])

  useEffect(() => {
    loadSubscriptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.per_page, filters.plan_id, filters.status, filters.expires_from, filters.expires_to, filters.sort, filters.order, filters.search])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((prev) => {
        const nextSearch = searchInput.trim() || undefined
        if (prev.search === nextSearch) return prev
        return { ...prev, search: nextSearch, page: 1 }
      })
    }, 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  const handlePageChange = (_: unknown, newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage + 1 }))
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const perPage = parseInt(e.target.value, 10)
    setFilters((prev) => ({ ...prev, per_page: perPage, page: 1 }))
  }

  const openMenu = (e: React.MouseEvent<HTMLElement>, sub: UserSubscription) => {
    e.stopPropagation()
    setMenuAnchor(e.currentTarget)
    setMenuSub(sub)
  }

  const closeMenu = () => {
    setMenuAnchor(null)
    setMenuSub(null)
  }

  const handleActivate = async (sub: UserSubscription) => {
    closeMenu()
    setActioningId(sub.id)
    setError(null)
    try {
      await subscriptionsApi.activateSubscription(sub.id)
      await loadSubscriptions()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string }; status?: number }; message?: string }
      setError(e?.response?.data?.message || e?.message || t('common.error'))
    } finally {
      setActioningId(null)
    }
  }

  const handleDeactivate = async (sub: UserSubscription) => {
    closeMenu()
    setActioningId(sub.id)
    setError(null)
    try {
      await subscriptionsApi.deactivateSubscription(sub.id)
      await loadSubscriptions()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string }; status?: number }; message?: string }
      setError(e?.response?.data?.message || e?.message || t('common.error'))
    } finally {
      setActioningId(null)
    }
  }

  const handleExtendAll = async () => {
    const days = parseInt(extendDays, 10)
    if (days < 1) return
    setExtendSubmitting(true)
    setError(null)
    try {
      await subscriptionsApi.extendAllSubscriptions(days)
      setExtendDialogOpen(false)
      setExtendDays('7')
      await loadSubscriptions()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string }; status?: number }; message?: string }
      setError(e?.response?.data?.message || e?.message || t('common.error'))
    } finally {
      setExtendSubmitting(false)
    }
  }

  const openExtendOneDialog = (sub: UserSubscription) => {
    closeMenu()
    setExtendOneSub(sub)
    setExtendOneDays('7')
    setExtendOneDialogOpen(true)
  }

  const handleExtendOne = async () => {
    if (!extendOneSub) return
    const days = parseInt(extendOneDays, 10)
    if (days < 1) return
    setExtendOneSubmitting(true)
    setError(null)
    try {
      await subscriptionsApi.extendSubscription(extendOneSub.id, days)
      setExtendOneDialogOpen(false)
      setExtendOneSub(null)
      await loadSubscriptions()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string }; status?: number }; message?: string }
      setError(e?.response?.data?.message || e?.message || t('common.error'))
    } finally {
      setExtendOneSubmitting(false)
    }
  }

  const userName = (sub: UserSubscription) => {
    const u = sub.user
    if (!u) return `#${sub.id}`
    return getUserDisplayName(u, { prefer: 'full_name' })
  }

  const statusLabel = (sub: UserSubscription) => {
    if (sub.payment?.status === 'pending') return t('admin.subscriptions.pendingPayment')
    if (sub.is_active) return t('admin.subscriptions.active')
    return t('admin.subscriptions.inactive')
  }

  const statusChipColor = (sub: UserSubscription): 'success' | 'default' | 'warning' => {
    if (sub.payment?.status === 'pending') return 'warning'
    if (sub.is_active) return 'success'
    return 'default'
  }

  const paginationLabelDisplayedRows = ({ from, to, count }: { from: number; to: number; count: number }) =>
    t('admin.subscriptions.displayedRows', { from, to, count })

  return (
    <DashboardLayout>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ mb: 2 }}
          >
            <Link
              to={ROUTES.DASHBOARD}
              style={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {t('dashboard.home')}
            </Link>
            <Typography color="text.primary">{t('dashboard.administration')}</Typography>
            <Typography color="text.primary">{t('admin.subscriptions.title')}</Typography>
          </Breadcrumbs>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
            <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
              {t('admin.subscriptions.title')}
            </Typography>
            <PageHelpPopover pageKey="admin-subscriptions" />
          </Box>

          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder={t('admin.subscriptions.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: isMobile ? 120 : 220, flex: isMobile ? 1 : undefined }}
            />
            <FormControl size="small" sx={{ minWidth: isMobile ? 140 : 200 }}>
              <InputLabel>{t('admin.subscriptions.filterSort')}</InputLabel>
              <Select
                value={filters.sort && filters.order ? `${filters.sort}_${filters.order}` : 'expires_at_desc'}
                label={t('admin.subscriptions.filterSort')}
                onChange={(e) => {
                  const [sort, order] = parseSortValue(e.target.value as string)
                  setFilters((prev) => ({ ...prev, sort, order, page: 1 }))
                }}
              >
                <MenuItem value="expires_at_desc">{t('admin.subscriptions.sortExpiresAt')} (↓)</MenuItem>
                <MenuItem value="expires_at_asc">{t('admin.subscriptions.sortExpiresAt')} (↑)</MenuItem>
                <MenuItem value="created_at_desc">{t('admin.subscriptions.sortCreatedAt')} (↓)</MenuItem>
                <MenuItem value="created_at_asc">{t('admin.subscriptions.sortCreatedAt')} (↑)</MenuItem>
                <MenuItem value="starts_at_desc">{t('admin.subscriptions.sortStartsAt')} (↓)</MenuItem>
                <MenuItem value="starts_at_asc">{t('admin.subscriptions.sortStartsAt')} (↑)</MenuItem>
              </Select>
            </FormControl>
            <FiltersButton
              open={filtersOpen}
              count={appliedFiltersCount}
              onToggle={() => setFiltersOpen((o) => !o)}
              label={t('admin.subscriptions.filtersButton')}
            />
            <Box sx={{ ml: isMobile ? 0 : 'auto', display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => setExtendDialogOpen(true)}
                sx={{ height: 40 }}
              >
                {t('admin.subscriptions.extendAll')}
              </Button>
            </Box>
          </Box>

          <Collapse in={filtersOpen}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
                <InputLabel>{t('admin.subscriptions.filterPlan')}</InputLabel>
                <Select
                  value={filters.plan_id ?? ''}
                  label={t('admin.subscriptions.filterPlan')}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      plan_id: String(e.target.value) === '' ? undefined : Number(e.target.value),
                      page: 1,
                    }))
                  }
                >
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  {plans.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
                <InputLabel>{t('admin.subscriptions.filterStatus')}</InputLabel>
                <Select
                  value={filters.status ?? ''}
                  label={t('admin.subscriptions.filterStatus')}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      status: String(e.target.value) === '' ? undefined : (e.target.value as AdminSubFilters['status']),
                      page: 1,
                    }))
                  }
                >
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  <MenuItem value="active">{t('admin.subscriptions.active')}</MenuItem>
                  <MenuItem value="inactive">{t('admin.subscriptions.inactive')}</MenuItem>
                  <MenuItem value="pending_payment">{t('admin.subscriptions.pendingPayment')}</MenuItem>
                </Select>
              </FormControl>
              <DatePicker
                label={t('admin.subscriptions.filterExpiresFrom')}
                value={filters.expires_from ? dayjs(filters.expires_from) : null}
                onChange={(date: Dayjs | null) =>
                  setFilters((prev) => ({
                    ...prev,
                    expires_from: date ? date.format('YYYY-MM-DD') : undefined,
                    page: 1,
                  }))
                }
                slotProps={{ textField: { size: 'small', sx: { minWidth: { xs: '100%', sm: 160 } } } }}
              />
              <DatePicker
                label={t('admin.subscriptions.filterExpiresTo')}
                value={filters.expires_to ? dayjs(filters.expires_to) : null}
                onChange={(date: Dayjs | null) =>
                  setFilters((prev) => ({
                    ...prev,
                    expires_to: date ? date.format('YYYY-MM-DD') : undefined,
                    page: 1,
                  }))
                }
                slotProps={{ textField: { size: 'small', sx: { minWidth: { xs: '100%', sm: 160 } } } }}
              />
            </Box>
          </Collapse>

          {loading ? (
            isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Skeleton variant="text" width={80} height={20} />
                          <Skeleton variant="text" width="70%" height={22} sx={{ mt: 0.5 }} />
                          <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 1, mt: 0.5 }} />
                          <Skeleton variant="text" width={100} height={18} sx={{ mt: 0.5 }} />
                        </Box>
                        <Skeleton variant="circular" width={32} height={32} />
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>{t('admin.subscriptions.user')}</TableCell>
                      <TableCell>{t('admin.subscriptions.plan')}</TableCell>
                      <TableCell>{t('admin.subscriptions.status')}</TableCell>
                      <TableCell>{t('admin.subscriptions.startsAt')}</TableCell>
                      <TableCell>{t('admin.subscriptions.expiresAt')}</TableCell>
                      <TableCell align="right">{t('admin.users.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton variant="text" width={24} /></TableCell>
                        <TableCell><Skeleton variant="text" width={120} /></TableCell>
                        <TableCell><Skeleton variant="text" width={90} /></TableCell>
                        <TableCell><Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                        <TableCell><Skeleton variant="text" width={90} /></TableCell>
                        <TableCell><Skeleton variant="text" width={90} /></TableCell>
                        <TableCell align="right"><Skeleton variant="circular" width={32} height={32} sx={{ ml: 'auto' }} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {subscriptions.length === 0 ? (
                <Typography color="text.secondary">{t('admin.subscriptions.noSubscriptions')}</Typography>
              ) : (
                subscriptions.map((sub) => (
                  <Card key={sub.id}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            #{sub.id} · {sub.plan?.name ?? '-'}
                          </Typography>
                          <Typography variant="body1">{userName(sub)}</Typography>
                          <Chip
                              size="small"
                              label={statusLabel(sub)}
                              color={statusChipColor(sub)}
                              sx={{ mt: 0.5 }}
                            />
                          <Typography variant="body2" color="text.secondary">
                            {t('admin.subscriptions.expiresAt')}:{' '}
                            {sub.expires_at ? format(new Date(sub.expires_at), 'dd.MM.yyyy', { locale: dateLocale }) : '-'}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => openMenu(e, sub)}
                          aria-label={t('common.actions')}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 2,
                  overflow: 'hidden',
                  minWidth: 0,
                  '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', rowGap: 1, justifyContent: 'flex-end' },
                }}
              >
                <TablePagination
                  component="div"
                  count={meta.total}
                  page={meta.current_page - 1}
                  onPageChange={handlePageChange}
                  rowsPerPage={meta.per_page}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={[10, 15, 25, 50]}
                  labelRowsPerPage={t('admin.subscriptions.rowsPerPage')}
                  labelDisplayedRows={paginationLabelDisplayedRows}
                />
              </Box>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>{t('admin.subscriptions.user')}</TableCell>
                      <TableCell>{t('admin.subscriptions.plan')}</TableCell>
                      <TableCell>{t('admin.subscriptions.status')}</TableCell>
                      <TableCell>{t('admin.subscriptions.startsAt')}</TableCell>
                      <TableCell>{t('admin.subscriptions.expiresAt')}</TableCell>
                      <TableCell align="right">{t('admin.users.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subscriptions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          {t('admin.subscriptions.noSubscriptions')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      subscriptions.map((sub) => (
                        <TableRow key={sub.id}>
                          <TableCell>{sub.id}</TableCell>
                          <TableCell>
                            <Link to={ROUTES.ADMIN_USER_VIEW(sub.user?.id ?? 0)} style={{ color: 'inherit' }}>
                              {userName(sub)}
                            </Link>
                          </TableCell>
                          <TableCell>{sub.plan?.name ?? '-'}</TableCell>
<TableCell>
                            <Chip
                              size="small"
                              label={statusLabel(sub)}
                              color={statusChipColor(sub)}
                            />
                          </TableCell>
                        <TableCell>
                          {sub.starts_at
                              ? format(new Date(sub.starts_at), 'dd.MM.yyyy', { locale: dateLocale })
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {sub.expires_at
                              ? format(new Date(sub.expires_at), 'dd.MM.yyyy', { locale: dateLocale })
                              : '-'}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(e) => openMenu(e, sub)}
                              aria-label={t('common.actions')}
                              disabled={actioningId === sub.id}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 2,
                  overflow: 'hidden',
                  minWidth: 0,
                  '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', rowGap: 1, justifyContent: 'flex-end' },
                }}
              >
                <TablePagination
                  component="div"
                  count={meta.total}
                  page={meta.current_page - 1}
                  onPageChange={handlePageChange}
                  rowsPerPage={meta.per_page}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={[10, 15, 25, 50]}
                  labelRowsPerPage={t('admin.subscriptions.rowsPerPage')}
                  labelDisplayedRows={paginationLabelDisplayedRows}
                />
              </Box>
            </>
          )}

          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu} onClick={closeMenu}>
            {menuSub && (
              <>
                {menuSub.is_active ? (
                  <MenuItem
                    onClick={() => handleDeactivate(menuSub)}
                    disabled={actioningId === menuSub.id || menuSub.payment?.status === 'pending'}
                  >
                    <ListItemIcon>
                      <DeactivateIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('admin.subscriptions.deactivate')}</ListItemText>
                  </MenuItem>
                ) : menuSub.payment?.status !== 'pending' ? (
                  <MenuItem onClick={() => handleActivate(menuSub)} disabled={actioningId === menuSub.id}>
                    <ListItemIcon>
                      <ActivateIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('admin.subscriptions.activate')}</ListItemText>
                  </MenuItem>
                ) : null}
                <MenuItem onClick={() => openExtendOneDialog(menuSub)}>
                  <ListItemIcon>
                    <ExtendIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('admin.subscriptions.extendOne')}</ListItemText>
                </MenuItem>
              </>
            )}
          </Menu>

          <Dialog open={extendDialogOpen} onClose={() => !extendSubmitting && setExtendDialogOpen(false)}>
            <DialogTitle>{t('admin.subscriptions.extendAll')}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {t('admin.subscriptions.extendConfirm', { days: extendDays })}
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                label={t('admin.subscriptions.extendDays')}
                type="number"
                inputProps={{ min: 1 }}
                value={extendDays}
                onChange={(e) => setExtendDays(e.target.value)}
                fullWidth
                size="small"
                sx={{ mt: 2 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExtendDialogOpen(false)} disabled={extendSubmitting}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleExtendAll}
                variant="contained"
                disabled={extendSubmitting || parseInt(extendDays, 10) < 1}
              >
                {extendSubmitting ? t('common.loading') : t('admin.subscriptions.extendAll')}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={extendOneDialogOpen}
            onClose={() => !extendOneSubmitting && setExtendOneDialogOpen(false)}
          >
            <DialogTitle>{t('admin.subscriptions.extendOne')}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {extendOneSub
                  ? t('admin.subscriptions.extendOneConfirm', { days: extendOneDays })
                  : ''}
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                label={t('admin.subscriptions.extendOneDays')}
                type="number"
                inputProps={{ min: 1 }}
                value={extendOneDays}
                onChange={(e) => setExtendOneDays(e.target.value)}
                fullWidth
                size="small"
                sx={{ mt: 2 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExtendOneDialogOpen(false)} disabled={extendOneSubmitting}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleExtendOne}
                variant="contained"
                disabled={extendOneSubmitting || !extendOneSub || parseInt(extendOneDays, 10) < 1}
              >
                {extendOneSubmitting ? t('common.loading') : t('admin.subscriptions.extendOne')}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </LocalizationProvider>
    </DashboardLayout>
  )
}
